import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMakeOffer } from "../../GlobalContext/makeOfferContext";

export type ToggleOption = {
  label: string;
  checked: boolean;
};

interface ToggleColumnProps {
  mapToggleOptionsToRequestData: (toggleOptions: ToggleOption[]) => void;
  isFreeEntryAllowed: boolean;
  isFreeEntryChecked: boolean;
}

const createDefaultToggleOptions = (): ToggleOption[] => ([
  { label: "I will walk you in myself", checked: false },
  { label: "Free Entry", checked: false },
  { label: "Complimentary Drinks", checked: false },
  { label: "Free Dinner", checked: false },
  { label: "Free Taxi", checked: false },
  { label: "Queue Jump", checked: false },
  { label: "Is Other", checked: false },
]);

const ToggleColumn: React.FC<ToggleColumnProps> = ({
  mapToggleOptionsToRequestData,
  isFreeEntryAllowed,
  isFreeEntryChecked,
}) => {
  const { groupId } = useParams();
  const { getToggleOptions, setToggleOptions: persistToggleOptions } = useMakeOffer();
  const [toggleOptions, setToggleOptions] = useState<ToggleOption[]>(createDefaultToggleOptions);
  const [isHydratedFromContext, setIsHydratedFromContext] = useState<boolean>(false);

  useEffect(() => {
    const persistedToggleOptions = getToggleOptions(groupId);
    if (persistedToggleOptions) {
      setToggleOptions(persistedToggleOptions);
      mapToggleOptionsToRequestData(persistedToggleOptions);
    }
    setIsHydratedFromContext(true);
  }, [getToggleOptions, groupId, mapToggleOptionsToRequestData]);

  useEffect(() => {
    if (!groupId || !isHydratedFromContext) {
      return;
    }

    persistToggleOptions(groupId, toggleOptions);
  }, [groupId, isHydratedFromContext, persistToggleOptions, toggleOptions]);

  useEffect(() => {
    setToggleOptions((prevOptions) => {
      const freeEntryOption = prevOptions.find((option) => option.label === "Free Entry");
      const nextFreeEntryChecked = isFreeEntryAllowed ? isFreeEntryChecked : false;

      if (!freeEntryOption || freeEntryOption.checked === nextFreeEntryChecked) {
        return prevOptions;
      }

      const updatedOptions = prevOptions.map((option) =>
        option.label === "Free Entry"
          ? { ...option, checked: nextFreeEntryChecked }
          : option
      );

      mapToggleOptionsToRequestData(updatedOptions);
      return updatedOptions;
    });
  }, [isFreeEntryAllowed, isFreeEntryChecked, mapToggleOptionsToRequestData]);

  const handleToggleChange = (index: number) => {
    setToggleOptions((prevOptions) => {
      const updatedOptions = [...prevOptions];
      const currentOption = updatedOptions[index];

      if (currentOption.label === "Free Entry" && !isFreeEntryAllowed) {
        return prevOptions;
      }

      updatedOptions[index] = {
        ...currentOption,
        checked: !currentOption.checked,
      };

      mapToggleOptionsToRequestData(updatedOptions);
      return updatedOptions;
    });
  };

  return (
    <div className="mt-2 yellowTxt container">
      <div className="row">
        <div className="col">
          {toggleOptions.map((option, index) => {
            if (option.label === "Free Entry" && !isFreeEntryAllowed) {
              return null;
            }

            const isDisabled = option.label === "Free Entry" && !isFreeEntryAllowed;

            return (
              <div className="form-check mt-3 form-switch" key={option.label}>
                <input
                  className="form-check-input shadow-none"
                  type="checkbox"
                  checked={option.checked}
                  onChange={() => handleToggleChange(index)}
                  disabled={isDisabled}
                />
                <label
                  className={`form-check-label justify-content-start d-flex${isDisabled ? " opacity-50" : ""}`}
                  onClick={() => {
                    if (!isDisabled) {
                      handleToggleChange(index);
                    }
                  }}
                >
                  {option.label}
                </label>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ToggleColumn;
